export const TAKEABLE_ASSIGNMENT_STATUSES = new Set(['pending', 'scheduled', 'in_progress']);
export const NEPTUN_RE = /^[A-Z0-9]{6}$/i;

export type AssignmentScheduleKind = 'slot' | 'window' | 'none';

export type AssignmentWindowFields = {
  interview_id?: string | null;
  assignment_status?: string | null;
  interview_scheduled_at?: string | null;
  available_from?: string | null;
  available_until?: string | null;
};

export type InterviewWindowLookup = {
  id: string;
  available_from?: string | null;
  available_until?: string | null;
};

export function normalizeOptionalNeptun(raw?: string | null): { neptun: string | null; error: string | null } {
  const value = String(raw || '').trim();
  if (!value) {
    return { neptun: null, error: null };
  }
  if (!NEPTUN_RE.test(value)) {
    return { neptun: null, error: 'invalid Neptun code (6 alphanumeric characters)' };
  }
  return { neptun: value.toUpperCase(), error: null };
}

function lookupInterview(
  interviewId: string | null | undefined,
  interviewsById?: Map<string, InterviewWindowLookup>
): InterviewWindowLookup | undefined {
  if (!interviewId || !interviewsById) return undefined;
  return interviewsById.get(interviewId);
}

export function resolveAssignmentWindow(
  assignment: AssignmentWindowFields | null | undefined,
  interviewsById?: Map<string, InterviewWindowLookup>
): { available_from: string | null; available_until: string | null } {
  const fromAssignment = {
    available_from: assignment?.available_from || null,
    available_until: assignment?.available_until || null
  };
  if (fromAssignment.available_from || fromAssignment.available_until) {
    return fromAssignment;
  }
  const interview = lookupInterview(assignment?.interview_id, interviewsById);
  return {
    available_from: interview?.available_from || null,
    available_until: interview?.available_until || null
  };
}

export function isTakeableAssignment(
  assignment: AssignmentWindowFields | null | undefined,
  interviewsById?: Map<string, InterviewWindowLookup>
): boolean {
  if (!assignment) return false;
  const status = String(assignment.assignment_status || 'pending').toLowerCase();
  if (status === 'cancelled' || status === 'completed') return false;
  if (!TAKEABLE_ASSIGNMENT_STATUSES.has(status)) return false;
  if (assignment.interview_scheduled_at) return true;
  const window = resolveAssignmentWindow(assignment, interviewsById);
  return Boolean(window.available_from || window.available_until);
}

export function assignmentScheduleKind(
  assignment: AssignmentWindowFields | null | undefined,
  interviewsById?: Map<string, InterviewWindowLookup>
): AssignmentScheduleKind {
  if (!assignment) return 'none';
  if (assignment.interview_scheduled_at) return 'slot';
  const window = resolveAssignmentWindow(assignment, interviewsById);
  if (window.available_from || window.available_until) return 'window';
  return 'none';
}

export function candidateHasTakeableAssignment(
  assignments: AssignmentWindowFields[] | null | undefined,
  interviewsById?: Map<string, InterviewWindowLookup>
): boolean {
  return (assignments || []).some((assignment) => isTakeableAssignment(assignment, interviewsById));
}

export function isAssessmentWindowOpen(
  availableFrom?: string | null,
  availableUntil?: string | null,
  nowMs: number = Date.now()
): boolean {
  const fromMs = availableFrom ? new Date(availableFrom).getTime() : NaN;
  const untilMs = availableUntil ? new Date(availableUntil).getTime() : NaN;
  if (Number.isFinite(fromMs) && fromMs > nowMs) return false;
  if (Number.isFinite(untilMs) && untilMs <= nowMs) return false;
  return Number.isFinite(fromMs) || Number.isFinite(untilMs);
}

export function buildInterviewWindowMap(
  interviews: Array<InterviewWindowLookup> | null | undefined
): Map<string, InterviewWindowLookup> {
  return new Map((interviews || []).map((interview) => [interview.id, interview]));
}
